import { Injectable } from '@angular/core';

import { IObjectNumberKeyMap, IObjectStringKeyMap } from '@shared/models';
import { OutputValue } from '@shared/components/compare-value/model';
import { FilterPopupConfig, SelectFiltersFormComponent } from '@modules/reporting/components';
import { FolderDataType, FolderPickerInput } from '@modules/management/modules/folders/model/folders.interfaces';
import { DateRangePickerComponent, TimePickerComponent } from '@shared/components';
import { DeploymentPage } from '@modules/management/pages/details/check/pages/deployment/deployment.page';
import {
  CheckSubject,
  DeploymentFrequencyType,
  ICheck,
  ICheckGroup,
  IDeploymentCheckGroup
} from '@modules/management/pages/details/check/models';
import {
  QuestionsToIncludeFormComponent
} from '../../components/questions-to-include-form/questions-to-include-form.component';
import {
  FolderPickerComponent
} from '@modules/management/modules/folder-picker/components/folder-picker/folder-picker.component';
import {
  AccountsService,
  AssetsService,
  ChecksService,
  FormBuilderService,
  FormField,
  LoadingService,
  Module,
  NativeElementService,
  Permission,
  PermissionsService,
  PropertyService,
  RolesService,
  TeamsService,
  UserdataService,
  UserService,
  UtilsService,
  ViewerService,
  ZoneService
} from '@services';

import * as moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { TranslateService } from '@ngx-translate/core';
import {
  assign,
  clone,
  cloneDeep,
  difference,
  each,
  filter,
  find,
  findIndex,
  flatten,
  get,
  has,
  includes,
  indexOf,
  intersection,
  isArray,
  isEmpty,
  isNumber,
  isObject,
  isUndefined,
  keys,
  last,
  map,
  orderBy,
  pick,
  reject,
  remove,
  set,
  some,
  split,
  times,
  uniq
} from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class DeploymentFormService {
  public removedPropertyValueFields = [];
  public selectedPropertyValues = [];

  public typeOptions = [
    {
      id: CheckSubject.Assets,
      description: this.translate.instant('SHARED.ASSETS')
    },
    {
      id: CheckSubject.Zones,
      description: this.translate.instant('SHARED.Zones')
    },
    {
      id: CheckSubject.Workers,
      description: this.translate.instant('SHARED.Workers')
    }
  ];
  private readonly fieldPropertyValuePrefix: string = 'propertyValues_';
  private deploymentPage: DeploymentPage;
  private deploymentType: string;
  private reminderTriggers = [
    {
      id: 'expiring',
      description: this.translate.instant('MGMT_DETAILS.Remind_Expiring'),
      exclude: ['onDemand']
    },
    {
      id: 'missed',
      description: this.translate.instant('SHARED.Missed'),
      exclude: ['onDemand']
    },
    {
      id: 'completed',
      description: this.translate.instant('MGMT_DETAILS.Remind_Completed')
    },
    {
      id: 'negative',
      description: this.translate.instant('MGMT_DETAILS.Remind_Negative')
    },
  ];

  private presentationStyles = [
    {
      id: 'guided',
      description: this.translate.instant('MGMT_DETAILS.Guided')
    },
    {
      id: 'unordered',
      description: this.translate.instant('MGMT_DETAILS.Unordered'),
    }
  ];
  public propertyValueFields = this.getDynamicNumericPropertyFields();
  public fields: FormField[] = [
    {
      containerClass: 'type-selector',
      title: 'MGMT_DETAILS.Select_Deployment_Type',
      type: 'subtitle'
    },
    {
      containerClass: 'type-selector',
      title: 'SHARED.Deployment_Type',
      name: 'type',
      canClear: false,
      originalOrder: true,
      type: 'selectmenu',
      role: 'none',
      placeholder: this.translate.instant('MGMT_DETAILS.Select_Deployment_Type'),
      options: this.typeOptions,
      onChange: (type: string) => this.syncFieldsVisibilityByType(type)
    },
    {
      containerClass: 'deploy-field assets zones workers external-field field-disable',
      required: false,
      inputtype: 'verbatim',
      name: 'disabled-type',
      title: 'SHARED.Deployment_Type',
      type: 'text',
      description: 'MGMT_DETAILS.Target_Description',
    },
    {
      // CVX-6533: read-only frequency, shown only for external (MaintainX) deployments
      containerClass: 'deploy-field external-field field-disable',
      required: false,
      inputtype: 'verbatim',
      name: 'disabled-frequency',
      title: 'MGMT_DETAILS.Frequency',
      type: 'text'
    },
    {
      containerClass: 'deploy-field assets zones workers external-field',
      required: true,
      inputtype: 'verbatim',
      name: 'title',
      title: 'MGMT_DETAILS.Title',
      type: 'text'
    },
    {
      containerClass: 'deploy-field assets zones workers external-field',
      title: 'MGMT_LIST.Deployment_Active',
      name: 'active',
      type: 'flipswitch',
      value: 1,
      default: 0,
      disabledFieldNames: ['availableImmediately']
    },
    {
      containerClass: 'deploy-field assets external-field',
      title: 'MGMT_DETAILS.Asset_Target',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Target_Description'
    },
    {
      containerClass: 'deploy-field zones',
      title: 'MGMT_DETAILS.Zone_Target',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Target_Description'
    },
    {
      containerClass: 'deploy-field workers',
      title: 'MGMT_DETAILS.Worker_Target',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Target_Description'
    },
    {
      containerClass: 'deploy-field assets zones workers target-field',
      title: this.translate.instant('SHARED.Locationss'),
      name: 'locations',
      type: 'selectmenu',
      multiple: true,
      placeholder: this.translate.instant('SHARED.All_Locations'),
      valueProperty: 'locationID',
      options: [],
      func: (ref) => ref.name,
      onChange: (locationIds: any[] = []) => {
        this.resetAssetsFolders();
        this.onLocationChange(locationIds);
      }
    },
    {
      containerClass: 'deploy-field workers target-field',
      title: 'SHARED.Team(s)',
      name: 'teams',
      type: 'selectmenu',
      placeholder: this.translate.instant('MGMT_DETAILS.Choose_Team'),
      multiple: true,
      required: true,
      searchable: true,
      canClear: true,
      originalOrder: true,
      valueProperty: 'groupID',
      options: [],
      test: (team: any) => !get(team, 'disabledAt') && team.groupID !== -1,
      func: (team: any) => this.teamsService.teamName(team),
      onChange: (ids) => {
        this.removeWorkerAssignAnyOption(ids, 'teams');
        this.syncUsersField();
      }
    },
    // {
    //   containerClass: 'deploy-field workers target-field',
    //   name: 'certifications',
    //   title: 'SHARED.Certification(s)',
    //   placeholder: this.translate.instant('SHARED.Any_Certification'),
    //   type: 'selectmenu',
    //   multiple: true,
    //   valueProperty: 'certificationID',
    //   options: this.certificationsService.certifications,
    //   test: (ref) => {
    //     return !get(ref, 'disabledAt');
    //   },
    //   func: (certification: any) => this.formBuilderService.certificationName(certification)
    // },
    // {
    //   containerClass: 'deploy-field workers target-field',
    //   name: 'roles',
    //   title: 'MGMT_DETAILS.Roles',
    //   type: 'selectmenu',
    //   placeholder: this.translate.instant('MGMT_DETAILS.Any_Role'),
    //   multiple: true,
    //   valueProperty: 'roleID',
    //   options: this.roleService.roles.data,
    //   test: (ref) => !get(ref, 'disabledAt'),
    //   func: (role: any) => this.roleService.roleName(role),
    //   onChange: () => this.syncUsersField()
    // },
    {
      containerClass: 'deploy-field workers target-field',
      title: 'SHARED.EDIT_Permissions',
      name: 'permissions',
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.All_Permissions'),
      multiple: true,
      required: false,
      searchable: true,
      canClear: true,
      valueProperty: 'id',
      options: this.permissionsService.permissions.data,
      onChange: () => this.syncUsersField(),
      test: (ref) => this.filterPermission(ref)
    },
    {
      containerClass: 'deploy-field workers target-field',
      title: 'SHARED.User(s)',
      name: 'users',
      type: 'selectmenu',
      multiple: true,
      placeholder: this.translate.instant('SHARED.All_Users'),
      valueProperty: 'userID',
      options: [],
      func: (ref) => this.userService.getFullname(ref.userID),
      test: (ref) => !get(ref, 'disabledAt') && !includes([-1, -2], ref.userID),
      onChange: () => {
        const divideAssignmentField: any = find(this.fields, {name: 'divideAssignment'});

        if (divideAssignmentField && divideAssignmentField.getValue()) {
          const checkGroups = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];
          each(checkGroups, (checkGroup, index: number) => {
            this.syncUsersField(true, ++index);
          });
        } else {
          this.syncUsersField(true);
        }
      }
    },
    {
      containerClass: 'deploy-field assets zones target-field',
      title: 'SHARED.Zone(s)',
      name: 'zones',
      type: 'selectmenu',
      canClear: false,
      required: false,
      multiple: true,
      placeholder: this.translate.instant('SHARED.Any_Zone'),
      options: [],
      onChange: () => this.resetAssetsFolders()
    },
    {
      containerClass: 'deploy-field assets target-field asset-folders external-field',
      title: 'SHARED.Assets',
      name: 'assetsFolders',
      type: 'customElement',
      component: FolderPickerComponent,
      inputs: {
        folderPickerInput: {
          type: FolderDataType.ASSET,
          viewTree: {
            getSourceItems: () => this.syncAssetsField()
          }
        } as FolderPickerInput
      }
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: '',
      type: 'divider'
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Deployment_Assign_To_Title',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Deployment_Assign_To_Description'
    },
    /*     {
          containerClass: 'deploy-field assets zones workers',
          title: 'MGMT_DETAILS.Divide_Assignment',
          name: 'divideAssignment',
          type: 'flipswitch',
          value: 1,
          default: 0,
          onChange: () => this.onDivideAssignmentGroupsChange()
        }, */
    {
      containerClass: 'deploy-field assets zones workers assign-field',
      title: 'SHARED.Team(s)',
      name: 'assignedTo_teams',
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.Any_Team'),
      multiple: true,
      required: false,
      searchable: true,
      originalOrder: true,
      canClear: true,
      valueProperty: 'groupID',
      options: [],
      test: (team: any) => !get(team, 'disabledAt'),
      func: (team: any) => this.teamsService.teamName(team),
      onChange: (ids) => {
        this.removeWorkerAssignAnyOption(ids, 'assignedTo_teams');
        this.syncUsersField(true);
      }
    },
    {
      containerClass: 'deploy-field assets zones workers assign-field',
      title: 'MGMT_DETAILS.Allow_Secondary_Teams',
      name: 'assignedTo_anyTeam',
      type: 'flipswitch',
      value: 1,
      default: 0,
      onChange: () => {
      }
    },
    {
      containerClass: 'deploy-field assets zones workers assign-field',
      title: 'SHARED.EDIT_Permissions',
      name: 'assignedTo_permissions',
      type: 'selectmenu',
      placeholder: this.translate.instant('SHARED.Any_Permissions'),
      multiple: true,
      required: false,
      searchable: true,
      canClear: true,
      valueProperty: 'id',
      options: this.permissionsService.permissions.data,
      onChange: (ids: string[]) => {
        this.removeWorkerAssignAnyOption(ids, 'assignedTo_permissions');
        this.syncUsersField(true);
      },
      test: (ref) => this.filterPermission(ref)
    },
    // {
    //   containerClass: 'deploy-field assets zones workers assign-field',
    //   name: 'assignedTo_certifications',
    //   title: 'SHARED.Certification(s)',
    //   placeholder: this.translate.instant('SHARED.Any_Certification'),
    //   type: 'selectmenu',
    //   multiple: true,
    //   valueProperty: 'certificationID',
    //   options: this.certificationsService.certifications,
    //   test: (ref) => {
    //     return !get(ref, 'disabledAt');
    //   },
    //   func: (certification: any) => this.formBuilderService.certificationName(certification)
    // },
    {
      containerClass: 'deploy-field assets zones workers assign-field',
      name: 'assignedTo_roles',
      title: 'MGMT_DETAILS.Roles',
      type: 'selectmenu',
      placeholder: this.translate.instant('MGMT_DETAILS.Any_Role'),
      multiple: true,
      valueProperty: 'roleID',
      options: this.roleService.roles,
      test: (ref) => {
        return !get(ref, 'disabledAt');
      },
      func: (role: any) => this.roleService.roleName(role),
      onChange: () => this.syncUsersField(true)
    },
    {
      containerClass: 'deploy-field assets zones workers assign-field',
      title: 'SHARED.User(s)',
      name: 'assignedTo_users',
      type: 'selectmenu',
      multiple: true,
      placeholder: this.translate.instant('SHARED.Any_User'),
      valueProperty: 'userID',
      originalOrder: true,
      options: [],
      func: (ref) => this.getUserName(ref),
      test: (ref) => !get(ref, 'disabledAt'),
      onChange: () => {
        this.syncRemindWho();
        this.onSendReminderChange();
      }
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Completed_Check_Required_Groups',
      name: 'requireAllGroups',
      type: 'flipswitch',
      value: 1,
      default: 0,
      onChange: () => {
      }
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: '',
      type: 'divider'
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Frequency',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Frequency_Description'
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Frequency',
      name: 'frequency',
      type: 'selectmenu',
      required: true,
      originalOrder: true,
      canClear: false,
      default: DeploymentFrequencyType.Daily,
      options: [
        {
          id: DeploymentFrequencyType.Daily,
          description: this.translate.instant('SHARED.Daily')
        },
        {
          id: DeploymentFrequencyType.Weekly,
          description: this.translate.instant('SHARED.Weekly')
        },
        {
          id: DeploymentFrequencyType.Monthly,
          description: this.translate.instant('SHARED.Monthly')
        },
        {
          id: DeploymentFrequencyType.Quarterly,
          description: this.translate.instant('SHARED.Quarterly')
        },
        {
          id: DeploymentFrequencyType.Yearly,
          description: this.translate.instant('SHARED.Yearly')
        },
        {
          id: DeploymentFrequencyType.Once,
          description: this.translate.instant('SHARED.Once')
        },
        {
          id: DeploymentFrequencyType.Custom,
          description: this.translate.instant('SHARED.Custom')
        },
        {
          id: DeploymentFrequencyType.FromProperty,
          description: this.translate.instant('SHARED.From_Property')
        }
      ],
      onChange: () => this.onFrequencyChange(),
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['at-login-response'],
            valueFunction: (frequency) => this.isAtLoginResponseInvalid({ frequency })
          }
        ]
      }
    },
    {
      title: this.translate.instant('MGMT_DETAILS.Question_Timeout'),
      tooltip: this.translate.instant('MGMT_DETAILS.Question_Timeout_Tooltip'),
      name: 'timeout',
      type: 'timepicker',
      containerClass: 'deploy-field assets workers zones frequency-field onDemand',
        required: true,
        originalOrder: true,
        min: 1,
            options: [
              {
                id: 'seconds',
                description: this.translate.instant('SHARED.Seconds'),
              },
              {
                id: 'minutes',
                description: this.translate.instant('SHARED.Minutes'),
              },
              {
                id: 'hours',
                description: this.translate.instant('SHARED.Hours'),
              },
              {
                id: 'days',
                description: this.translate.instant('SHARED.Days'),
              }
            ]
    },
    {
      name: 'frequencyOnceTime',
      type: 'customElement',
      component: DateRangePickerComponent,
      containerClass: 'deploy-field workers frequency-field once',
      inputs: {
        isTimePicker: true,
        useUTC: true
      },
      required: true
    },
    {
      title: 'MGMT_DETAILS.At_Login',
      name: 'atLogin',
      type: 'flipswitch',
      value: 1,
      default: 0,
      containerClass: 'deploy-field workers frequency-field weekly monthly quarterly yearly once custom',
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['at-login-response'],
            valueFunction: (atLogin) => this.isAtLoginResponseInvalid({ atLogin })
          }
        ]
      }
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field daily multi-daily',
      title: 'MGMT_DETAILS.Checks_Per_Day',
      name: 'checksPerDay',
      type: 'selectmenu',
      originalOrder: true,
      canClear: false,
      default: '1',
      options: this.getChecksCountOptions(),
      onChange: (value: string) => this.onTimesPerDayChange(value),
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['at-login-response'],
            valueFunction: (checksPerDay) => this.isAtLoginResponseInvalid({ checksPerDay })
          }
        ]
      }
    },
    {
      containerClass: 'deploy-field workers frequency-field daily weekly monthly quarterly yearly once custom at-login-response',
      title: '',
      name: 'atLoginRequiredResponse',
      type: 'checkbox',
      role: 'none',
      class: 'inline-checkboxes',
      break: true,
      options: [
        {
          id: 1,
          description: 'MGMT_DETAILS.At_Login_Response'
        }
      ]
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field daily multi-daily',
      title: 'MGMT_DETAILS.Select_Days_To_Perform',
      name: 'daily',
      type: 'checkbox',
      role: 'none',
      required: true,
      class: 'inline-checkboxes',
      break: true,
      options: this.getDays()
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field daily',
      name: 'startingTime',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        label: 'MGMT_DETAILS.Make_Available_When',
        isDuration: false,
        default: '00:00',
        min: '00:00',
        max: '23:59'
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field daily',
      name: 'duration',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        label: 'MGMT_DETAILS.Available_How_Long',
        isDuration: true,
        default: '01:00',
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field multi-daily',
      name: 'multiStartingTime',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        label: 'MGMT_DETAILS.First_Available_When',
        tooltip: this.translate.instant('MGMT_DETAILS.First_Available_Tooltip'),
        isDuration: false,
        default: '00:00',
        min: '00:00',
        max: '23:59'
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field multi-daily',
      name: 'multiInterval',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        tooltip: this.translate.instant('MGMT_DETAILS.Time_Between_Tooltip'),
        label: 'MGMT_DETAILS.Time_Between_Checks',
        isDuration: true,
        default: '00:00',
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field multi-daily',
      name: 'multiDuration',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        tooltip: this.translate.instant('MGMT_DETAILS.Each_How_Long_Tooltip'),
        label: 'MGMT_DETAILS.Each_Available_How_Long',
        isDuration: true,
        default: '00:00',
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field daily weekly monthly quarterly yearly custom',
      title: 'MGMT_DETAILS.Available_Immediately',
      name: 'availableImmediately',
      type: 'flipswitch',
      value: 1,
      default: 0,
      canView: Permission.SuperAdmin
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field weekly',
      title: 'MGMT_DETAILS.Select_Week_Day_To_Start',
      name: 'weekly',
      type: 'radio',
      class: 'inline-checkboxes',
      default: 'sun',
      break: true,
      options: this.getDays()
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field monthly',
      title: 'MGMT_DETAILS.Select_Time_Month',
      name: 'monthly',
      type: 'radio',
      class: 'inline-checkboxes',
      default: 'beginning',
      break: true,
      options: [
        {
          id: 'all',
          description: this.translate.instant('MGMT_DETAILS.Entire_Month')
        },
        {
          id: 'beginning',
          description: this.translate.instant('MGMT_DETAILS.Beginning_Of_Month')
        },
        {
          id: 'end',
          description: this.translate.instant('MGMT_DETAILS.End_Of_Month')
        }
      ]
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field quarterly',
      title: 'MGMT_DETAILS.Select_Time_Quarter',
      name: 'quarterly',
      type: 'radio',
      class: 'inline-checkboxes',
      default: 'first',
      break: true,
      options: [
        {
          id: 'all',
          description: this.translate.instant('MGMT_DETAILS.All_Quarter')
        },
        {
          id: 'first',
          description: this.translate.instant('MGMT_DETAILS.First_Month')
        },
        {
          id: 'second',
          description: this.translate.instant('MGMT_DETAILS.Second_Month')
        },
        {
          id: 'third',
          description: this.translate.instant('MGMT_DETAILS.Third_Month')
        }
      ]
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field yearly',
      title: 'MGMT_DETAILS.Select_Month_Perform',
      name: 'yearly',
      type: 'radio',
      class: 'inline-checkboxes',
      default: 'jan',
      break: true,
      options: this.getMonths()
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field custom',
      title: 'MGMT_DETAILS.First_Available_When',
      name: 'startingDateTime',
      required: true,
      type: 'customElement',
      component: DateRangePickerComponent,
      inputs: {
        singleDatePicker: true,
        isTimePicker: true,
        useUTC: true
      }
    },
    {
      title: 'MGMT_DETAILS.Redeploy_Immediately',
      tooltip: 'MGMT_DETAILS.Redeploy_Immediately_Tooltip',
      name: 'customRedeployImmediately',
      type: 'flipswitch',
      value: 1,
      default: 0,
      containerClass: 'deploy-field assets zones workers frequency-field custom',
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['frequency-field.custom-interval'],
            valueFunction: (val) => !!val,
          }
        ]
      }
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field custom',
      name: 'customDuration',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        tooltip: this.translate.instant('MGMT_DETAILS.Each_How_Long_Tooltip'),
        label: 'MGMT_DETAILS.Each_Available_How_Long',
        longDuration: true,
        isDuration: true,
        default: 'PT00:00',
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field custom custom-interval',
      name: 'customInterval',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        tooltip: this.translate.instant('MGMT_DETAILS.Custom_Time_Between_Tooltip'),
        label: 'MGMT_DETAILS.Time_Between_Checks',
        longDuration: true,
        isDuration: true,
        default: 'PT00:00',
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty',
      title: 'SHARED.Duration',
      type: 'subtitle'
    },
    {
      title: 'MGMT_DETAILS.Until_Completed',
      tooltip: 'MGMT_DETAILS.Until_Completed',
      name: 'fromPropertyUntilCompleted',
      type: 'flipswitch',
      value: 1,
      default: 0,
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty',
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['from-property-duration'],
            valueFunction: (val) => !!val,
          }
        ]
      }
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty from-property-duration',
      name: 'fromPropertyDuration',
      required: true,
      type: 'customElement',
      component: TimePickerComponent,
      inputs: {
        tooltip: this.translate.instant('MGMT_DETAILS.Each_How_Long_Tooltip'),
        label: 'MGMT_DETAILS.Each_Available_How_Long',
        longDuration: true,
        isDuration: true,
        default: 'PT00:00'
      },
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty',
      title: this.translate.instant('PROPERTY.Property_values'),
      name: 'propertyValues',
      type: 'customElement',
      component: SelectFiltersFormComponent,
      inputs: {
        options: cloneDeep(this.propertyValueFields),
        selectedItems: [],
        popupConfig: <FilterPopupConfig>{
          title: this.translate.instant('PROPERTY.Property_values'),
          subTitle: '',
          saveButton: this.translate.instant('SHARED.Add')
        },
        btnTitle: this.translate.instant('SHARED.Add')
      },
      outputs: {
        onChanged: (selectedItems) => {
          const field = <FormField>find(this.fields, {name: 'propertyValues'});
          (field.componentRef as SelectFiltersFormComponent).selectedItems = selectedItems;

          this.removedPropertyValueFields = difference(this.selectedPropertyValues, selectedItems);
          this.selectedPropertyValues = selectedItems;

          setTimeout(() => {
            // CVX-6062: page may be torn down before this fires — formConfig gone, bail.
            if (!this.hasFormConfig) { return; }
            const selectedFields = filter(this.propertyValueFields, (field) => includes(this.selectedPropertyValues, field.name));
            each(this.removedPropertyValueFields, (item) => {
              $(`#${this.formPrefix}${item}_container`).remove();
            });
            this.formBuilderService.addFieldsTo((<any>this.deploymentPage).formConfig, selectedFields, $(`#${this.formPrefix}${field.name}_container`));
          });
        }
      },
      options: cloneDeep(this.propertyValueFields)
    },
    {
      title: 'MGMT_DETAILS.Send_Reminder_Notifications',
      name: 'reminderNotification',
      type: 'flipswitch',
      value: 1,
      default: 0,
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty daily multi-daily weekly monthly quarterly yearly onDemand once custom',
      onChange: () => this.onSendReminderChange()
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty daily multi-daily weekly monthly quarterly yearly onDemand once custom',
      title: 'MGMT_DETAILS.Remind_What',
      name: 'remindWhat',
      type: 'checkbox',
      role: 'none',
      required: true,
      class: 'inline-checkboxes',
      break: true,
      options: this.reminderTriggers,
      onChange: (values, theForm) => this.onTriggerChange(values, theForm),
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty daily multi-daily weekly monthly quarterly yearly onDemand once custom',
      title: 'MGMT_DETAILS.Remind_Who',
      name: 'remindWho',
      type: 'selectmenu',
      multiple: true,
      placeholder: this.translate.instant('MGMT_DETAILS.Select_Users'),
      valueProperty: 'userID',
      originalOrder: true,
      options: this.getNotificationList(this.getUserLocations()),
      func: (ref) => this.getUserName(ref),
      test: (ref) => {
        if (get(ref, 'disabledAt') || ref.type === 'observer' || ref.type === 'observation' || ref.type === 'reporter' || ref.type === 'viewer') {
          return false;
        } else {
          return true;
        }
      },
      onChange: (values) => null
    },
    {
      containerClass: 'deploy-field assets zones workers frequency-field fromProperty daily multi-daily weekly monthly quarterly yearly once custom',
      title: 'MGMT_DETAILS.Remind_When',
      name: 'remindWhen',
      required: false,
      canClear: true,
      default: 0,
      originalOrder: true,
      type: 'timepicker',
      min: 0,
      options: [
        {
          id: 'minutes',
          description: this.translate.instant('SHARED.Minutes'),
        },
        {
          id: 'hours',
          description: this.translate.instant('SHARED.Hours'),
        },
        {
          id: 'days',
          description: this.translate.instant('SHARED.Days'),
        },
      ]
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: '',
      type: 'divider'
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Check_Presentation',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Check_Presentation_Description'
    },
    // {
    //   containerClass: 'deploy-field assets zones workers', // TODO [azyulikov] add .presentation-type-field after the first iteration
    //   title: 'MGMT_DETAILS.Presentation_Type',
    //   name: 'presentationType',
    //   type: 'selectmenu',
    //   originalOrder: true,
    //   canClear: false,
    //   default: 'standard',
    //   options: [
    //     {
    //       id: 'standard',
    //       description: this.translate.instant('MGMT_DETAILS.Standard_Check')
    //     },
    //     {
    //       id: 'quick',
    //       description: this.translate.instant('MGMT_DETAILS.Quick_Check')
    //     }
    //   ],
    //   onChange: (values) => null
    // },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Presentation_Type',
      name: 'presentationStyle',
      canClear: false,
      type: 'selectmenu',
      originalOrder: true,
      default: 'guided',
      options: this.presentationStyles,
      onChange: () => null,
      infoAction: (field) => {
        const videoType = field.getValue() === 'guided' ? 'guided' : 'advanced';
        const url = `/assets/images/deploymentPresentationType/${videoType}-example.mp4`;

        this.viewerService.showByType(url, 'video/mp4');
      }
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Show_Check_History',
      name: 'showCheckHistory',
      type: 'flipswitch',
      value: 1,
      default: 0,
      onChange: () => this.onCheckHistoryChange()
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Previous_Checks',
      name: 'previousChecks',
      type: 'selectmenu',
      originalOrder: true,
      canClear: false,
      default: '7',
      options: times(7, (currentNumber: number) => {
        const id: string = (currentNumber + 1).toString();
        return {id, description: id};
      }),
      onChange: (values) => null
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'MGMT_DETAILS.Custom_Question_Selection',
      name: 'customQuestionSelection',
      type: 'flipswitch',
      value: 1,
      default: 0,
      hideFieldsOnValue: {
        select: [
          {
            hideClasses: ['question-field-group'],
            valueFunction: (val) => !val,
          }
        ]
      }
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: '',
      type: 'divider'
    },
    {
      containerClass: 'deploy-field assets zones workers',
      title: 'SHARED.Worker_Power_Title',
      type: 'subtitle',
      description: 'MGMT_DETAILS.Worker_Power_Description',
      module: Module.LOYALTY
    },
    {
      containerClass: 'deploy-field assets zones workers completed-check-field',
      title: 'SHARED.Completed_Check',
      name: `completedCheck`,
      type: 'selectmenu',
      default: 0,
      canClear: false,
      options: times(6, (point: number) => {
        const description: string = this.translate.instant('MGMT_DETAILS.Points');
        return {id: point, description: `${point} ${description}`};
      }),
      onChange: (values) => null
    }
  ];

  private nativeElement: any;
  private readonly targetObjectMap: string[] = [
    'locations',
    'zones',
    'assets',
    'teams',
    'users',
    'permissions'
    // 'certifications',
    // 'roles',
  ];

  private questionsToInclude: IObjectNumberKeyMap<any> = {};
  // CVX-6533: frequency of the external (MaintainX) deployment being edited, sent back unchanged
  private externalFrequency: IObjectStringKeyMap<any>;

  constructor(
    private logger: NGXLogger,
    private zoneService: ZoneService,
    private userService: UserService,
    private userDataService: UserdataService,
    private assetsService: AssetsService,
    private teamsService: TeamsService,
    private accountService: AccountsService,
    private translate: TranslateService,
    private formBuilderService: FormBuilderService,
    private checkService: ChecksService,
    private loadingService: LoadingService,
    private nativeElementService: NativeElementService,
    private permissionsService: PermissionsService,
    private utils: UtilsService,
    private viewerService: ViewerService,
    private roleService: RolesService,
    private propertyService: PropertyService
  ) {}

  public getFields(deploymentPage: DeploymentPage): any {
    this.deploymentPage = deploymentPage;
    this.nativeElement = (<any>this.deploymentPage).elementRef.nativeElement;

    if (!this.externalFrequency) {
      this.addAssignmentGroups();
      this.addCheckGroups();
    }
    this.updateFieldLocationData();
    this.checkFrequencyField();

    return this.fields;
  }

  public decodeData(deploymentData): any {
    const formData: any = {};

    this.decodeTarget(formData, deploymentData);
    this.decodeAssigment(formData, deploymentData);
    this.decodeFrequency(formData, deploymentData);
    this.decodeCheckGroups(formData, deploymentData);

    formData.completedCheck = get(deploymentData, 'powerpoints');
    formData.previousChecks = get(deploymentData, 'showHistory');
    formData.requireAllGroups = get(deploymentData, 'requireAllGroups');
    formData.title = deploymentData.title;
    formData.active = deploymentData.active;
    formData.presentationStyle = deploymentData.presentationStyle;

    if (formData.previousChecks) {
      formData.showCheckHistory = 1;
    }

    this.onLocationChange(formData.locations);

    return formData;
  }

  public encodeData(formData): any {
    const formFieldKeys: string[] = [
      'title',
      'checkID',
      'target',
      'assignees',
      'frequency',
      'checkGroups',
      'showHistory',
      'powerpoints',
      'requireAllGroups',
      'active',
      'presentationStyle'
    ];
    this.encodeAssignment(formData);
    this.encodeFrequency(formData);
    this.encodeTarget(formData);
    this.encodeCheckGroups(formData);

    formData.showHistory = +formData.previousChecks || 0;
    formData.powerpoints = +formData.completedCheck || 0;
    formData.requireAllGroups = +formData.requireAllGroups || 0;
    formData.active = formData.active || 0;

    return pick(formData, formFieldKeys);
  }

  public setDeploymentType(type: string) {
    this.deploymentType = type;
  }

  public syncFieldsVisibilityByType(type?: string): void {
    this.deploymentType = type;
    this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.deploy-field', false);

    if (type) {
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.type-selector', false);
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, `.deploy-field.${type}`, true);

      this.onDivideAssignmentGroupsChange();
      this.onFrequencyChange();
      this.syncFrequencyPropertyValueFields();
      // this.definePresentationPreviewControl(); // TODO [azyulikov] disabled for the first iteration
      this.defineCompletedCheckField();
      this.onCheckHistoryChange();
      this.onSendReminderChange(<string>this.getFieldValue('frequency'));
      this.checkRequiredAllGroupsField();
      this.onTimesPerDayChange(this.getFieldValue('checksPerDay'));

      if (type === 'zones') {
        this.updateZonesField();
      }

      if (type === 'workers') {
        this.prepareWorkerFields();
      }

      this.updateChecksPerDayFieldByType(type);
      this.formBuilderService.changeFieldValueByName(this.nativeElement, 'disabled-type', find(this.typeOptions, ['id', type]).description);

      if (this.externalFrequency) {
        // CVX-6533: MaintainX owns schedule, assignment and questions - only the target assets and active stay editable
        this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.deploy-field', false);
        this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.deploy-field.external-field', true);
        this.nativeElementService.setElementVisibilityBy(this.nativeElement, '.completed-check-title-block', false);
        this.formBuilderService.changeFieldValueByName(this.nativeElement, 'disabled-frequency', this.translate.instant('SHARED.External_MaintainX'));
      }
    }
    this.nativeElementService.setElementVisibilityBy(this.nativeElement, '.ui-form-controls', !!type);
  }

  private syncFrequencyPropertyValueFields() {
    setTimeout(() => {
      const field = <FormField>find(this.fields, {name: 'propertyValues'});
      if (field?.componentRef) {
        (field.componentRef as SelectFiltersFormComponent).selectedItems = this.selectedPropertyValues;
      }
    });
  }

  private updateChecksPerDayFieldByType(type: string) {
    if (includes(['zones', 'assets'], type)) {
      const options = filter(this.getChecksCountOptions(), (option) => +option.id >= 0);
      this.updateFieldByName('checksPerDay', options);
    }
  }

  private getDays(): any[] {
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const list = moment.weekdaysShort();
    const ret = [];
    each(list, (day, idx) => {
      ret.push({
        id: days[idx],
        description: day
      });
    });
    return ret;
  }

  private getMonths(): any[] {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const monthList = moment.monthsShort();
    const ret = [];
    each(monthList, (month, idx) => {
      ret.push({
        id: months[idx],
        description: month
      });
    });
    return ret;
  }

  private encodeTarget(formData) {
    formData.target = {
      targetType: this.deploymentType
    };

    if (formData.teams) {
      formData.teams = filter(formData.teams, (team: string) => +team > 0);
    }

    each(this.targetObjectMap, (key: string) => {
      formData.target[key] = formData[key] ? flatten([formData[key]]) : [];
    });

    this.makeArray(formData.target);

    if (formData.assetsFolders) {
      let assetsFolders: { assetID?: number; folderID?: number; }[] = [];

      try {
        assetsFolders = JSON.parse(formData.assetsFolders);
      } catch (e) {}

      formData.target.assets = map(filter(assetsFolders, (item) => item.assetID !== undefined), 'assetID');
      formData.target.folders = map(filter(assetsFolders, (item) => item.folderID !== undefined), 'folderID');
    }
    formData.target = JSON.stringify(formData.target);
  }

  private encodeAssignment(formData: IObjectStringKeyMap<any>): void {
    if (!has(formData, 'divideAssignment')) {
      formData.divideAssignment = 0;
    }
    const assignmentFieldKey = 'assignedTo_';

    each(formData, (fieldValue: any, fieldKey: string) => {
      if (includes(fieldKey, assignmentFieldKey)) {
        const splitSeparator: string = formData.divideAssignment ? `${assignmentFieldKey}group` : assignmentFieldKey;
        const splitFieldKey: string = last(split(fieldKey, splitSeparator));
        let targetKey = `assignees[0][${splitFieldKey}]`;

        if (formData.divideAssignment) {
          const [groupIndex, groupKey] = split(splitFieldKey, '_');
          targetKey = `assignees[${+groupIndex - 1}][${groupKey}]`;
        }
        if (splitFieldKey === 'anyTeam') {
          set(formData, targetKey, +fieldValue);
        } else {
          set(formData, targetKey, flatten([fieldValue]));
        }
      }
    });

    if (formData.divideAssignment) {
      const groups: ICheckGroup[] = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];
      each(formData.assignees, (assignee: any, index: number) => {
        assignee.groupID = get(groups[index], 'groupID');
      });
    }

    if (isUndefined(formData.assignees)) {
      formData.assignees = [{}];
    }

    if (formData.assignees && formData.assignees.length) {
      each(formData.assignees, (item) => {
        if (this.deploymentType === 'workers') {
          each(['permissions', 'teams'], (field) => {
            if (item[field]) {
              item[field] = this.filterWorkerOptions(item[field]);
            }
          });
        }
        this.makeArray(item);
      });
      formData.assignees = JSON.stringify(formData.divideAssignment ? formData.assignees : formData.assignees[0]);
    }
  }

  private encodeCheckGroups(formData: IObjectStringKeyMap<any>): void {
    // first check to see if the control is enabled
    if (has(formData, 'customQuestionSelection') && formData.customQuestionSelection !== '') {
      // it is on
      const groups: ICheckGroup[] = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];
      const theData: IDeploymentCheckGroup[] = [];
      // loop through the check groups in check order
      each(groups, theGroup => {
        if (has(this.questionsToInclude, theGroup.groupID)) {
          // look in the structure to see if there are any
          if (this.questionsToInclude[theGroup.groupID].length) {
            const qlist = [];
            each(this.questionsToInclude[theGroup.groupID], item => {
              qlist.push(item.id);
            });
            theData.push({
              groupID: theGroup.groupID,
              questions: qlist
            });
          }
        }
      })
      formData['checkGroups'] = theData;
      // if there are, push them into the deployment contentGroups attribute
    } else {
      formData['checkGroups'] = [];
    }
  }

  private isAtLoginResponseInvalid({ frequency, atLogin, checksPerDay }: { frequency?: string, atLogin?: number, checksPerDay?: string }) {
    const frequencyValue: string = frequency || this.getFieldValue('frequency');
    const atLoginValue: number = atLogin || this.getFieldValue('atLogin', true);
    const checksPerDayValue: string = checksPerDay || this.getFieldValue('checksPerDay');

    return frequencyValue === 'onDemand' ||
      frequencyValue === 'daily' && checksPerDayValue !== '-1' ||
      !includes(['onDemand', 'daily'], frequencyValue) && atLoginValue !== 1;
  }

  private encodeFrequency(formData: any): void {
    if (this.externalFrequency) {
      formData.frequency = JSON.stringify(this.externalFrequency);
      return;
    }

    formData.frequency = {
      type: formData.frequency,
      count: +formData.checksPerDay || 1,
      timeout: +formData.timeout || 600,
      timeout_unit: formData.timeout_unit || 'seconds',
      startingTime: '00:00',
      duration: '00:00',
      reminder: +formData.reminderNotification === 1,
      remindWho: formData.remindWho,
      remindWhat: formData.remindWhat,
      availableImmediately: +formData.availableImmediately || 0,
      remindWhen: +formData.remindWhen,
      remindWhen_unit: formData.remindWhen_unit
    };

    if (formData.frequency.type === 'onDemand') {
      formData.frequency.remindWhen = 0;
    } else if (formData.frequency.type === 'once') {
      const frequencyOnceTime = formData.frequencyOnceTime ? JSON.parse(formData.frequencyOnceTime) : {};

      formData.frequency.availableAt = this.utils.toSeconds(get(frequencyOnceTime, 'startDate') || moment().valueOf());
      formData.frequency.availableUntil = this.utils.toSeconds(get(frequencyOnceTime, 'endDate') || moment().valueOf());
    }

    if (formData.frequency.type === 'daily') {
      if (formData.frequency.count === 1) {
        formData.frequency.startingTime = formData.startingTime.replace(/"/g, '');
        formData.frequency.duration = formData.duration.replace(/"/g, '');
        formData.frequency.interval = '00:00';
      } else {
        formData.frequency.startingTime = formData.multiStartingTime.replace(/"/g, '');
        formData.frequency.duration = formData.multiDuration.replace(/"/g, '');
        formData.frequency.interval = formData.multiInterval.replace(/"/g, '');
      }
    }

    if (includes([
      DeploymentFrequencyType.Monthly,
      DeploymentFrequencyType.Quarterly,
      DeploymentFrequencyType.Yearly
    ], formData.frequency.type)) {
      formData.frequency.when = formData[formData.frequency.type];
    }

    if (formData.frequency.type !== 'daily' && formData.frequency.type !== 'onDemand') {
      if (formData.atLogin) {
        formData.frequency.count = formData.atLoginRequiredResponse ? -2 : -1;
      }
    } else if (formData.frequency.type === 'daily') {
      if (+formData.checksPerDay === -1) {
        formData.frequency.count = formData.atLoginRequiredResponse ? -2 : -1;
      }
    }

    if (formData.frequency.type === 'custom') {
      // this has 8601 duration and interval values
      formData.frequency.startingDate = this.utils.toSeconds(formData.startingDateTime.replace(/"/g, ''));
      formData.frequency.duration = formData.customDuration.replace(/"/g, '');
      if (formData.customRedeployImmediately) {
        formData.frequency.interval = '-1';
      } else {
        formData.frequency.interval = formData.customInterval.replace(/"/g, '');
      }
    } else if (includes([DeploymentFrequencyType.Daily, DeploymentFrequencyType.Weekly], formData.frequency.type)) {
      formData.frequency.days = formData[formData.frequency.type];
      if (typeof (formData.frequency.days) !== 'object') {
        formData.frequency.days = [formData.frequency.days];
      }
    } else if (formData.frequency.type === DeploymentFrequencyType.FromProperty) {
      formData.frequency.untilCompleted = formData.fromPropertyUntilCompleted ? 1 : 0;
      const filters: IObjectStringKeyMap<OutputValue> = {};

      each(formData, (value, key) => {
        if (includes(key, this.fieldPropertyValuePrefix)) {
          const condition = this.propertyService.encodePropertyCondition(key, value);
          if (condition) {
            filters[key] = condition;
          }
        }
      });

      if (!formData.frequency.untilCompleted && formData.fromPropertyDuration) {
        formData.frequency.duration = formData.fromPropertyDuration.replace(/"/g, '');
      }
      formData.frequency.filters = filters;
    }

    formData.frequency = JSON.stringify(formData.frequency);
  }

  private makeArray(formFields) {
    each(formFields, (targetField, key) => {
      if (isArray(targetField)) {
        formFields[key] = map(targetField, (value) => isNaN(+value) ? value : +value);
      }
    });
  }

  private decodeTarget(formData: any, deploymentData: any) {
    const userLocations = this.getUserLocations();

    formData.type = get(deploymentData, 'target.targetType');
    assign(formData, pick(deploymentData.target, this.targetObjectMap));

    if (userLocations.length && get(formData, 'locations.length')) {
      formData.locations = intersection(userLocations, formData.locations);
    }

    if (deploymentData?.target?.targetType === 'workers' && !deploymentData?.target?.teams?.length) {
      deploymentData.target.teams = [-2];
    }

    formData.assetsFolders = [];
    each(deploymentData?.target?.assets, (assetID: number) => {
      formData.assetsFolders.push({ assetID });
    });
    each(deploymentData?.target?.folders, (folderID: number) => {
      formData.assetsFolders.push({ folderID });
    });
  }

  private decodeAssigment(formData: any, deploymentData: any) {
    let assignmentObject: any = get(deploymentData, 'assignedTo');

    if (has(assignmentObject, 'data')) {
      assignmentObject = JSON.parse(assignmentObject.data || null) || {};
    }

    if (assignmentObject) {
      if (isArray(assignmentObject)) {
        each(assignmentObject, (assignment, index: number) => {
          each(assignment, (ids: string[], key: string) => {
            formData[`assignedTo_group${index + 1}_${key}`] = ids;
          });
        });
        formData.divideAssignment = 1;
      } else if (isObject(assignmentObject)) {
        const requiredFieldKeys = ['permissions', 'teams', 'anyTeam'];
        const isWorker: boolean = this.deploymentType === 'workers';

        if (isWorker) {
          each(requiredFieldKeys, (fieldKey) => {
            if (!has(assignmentObject, fieldKey)) {
              if (fieldKey === 'anyTeam') {
                assignmentObject[fieldKey] = 0;
              } else {
                assignmentObject[fieldKey] = [];
              }
            }
          });
        }

        each(assignmentObject, (ids: string[], key: string) => {
          if (isWorker && includes(requiredFieldKeys, key) && ids.length === 0) {
            ids = ['-2'];
          }

          formData[`assignedTo_${key}`] = ids;
        });
      }
    }
  }

  private decodeCheckGroups(formData: IObjectStringKeyMap<any>, deploymentData: any): void {
    const theCheck = this.checkService.getCheck(deploymentData.checkID);
    const groups: ICheckGroup[] = get(theCheck, 'checkGroups') || [];
    const deploymentGroups: IDeploymentCheckGroup[] = get(deploymentData, 'checkGroups');

    if (deploymentGroups.length) {
      // this chec has picked questions
      formData.customQuestionSelection = 1;
      each(groups, groupDef => {
        const theID = groupDef.groupID;
        const selected = [];
        each(deploymentGroups, aGroup => {
          if (aGroup.groupID === theID) {
            each(aGroup.questions, aQuestion => {
              if (this.checkService.questions[aQuestion]) {
                //the question still exists
                selected.push({ id: aQuestion, description: this.userService.translateItem(this.checkService.questions[aQuestion], 'title')})
              }
            });
          }
        });
        this.questionsToInclude[theID] = selected;
      });
    }
  }

  private decodeFrequency(formData: any, deploymentData: any) {
    const frequency = (get(deploymentData, 'frequency') || {}) as any;

    formData.frequency = frequency.type;
    this.externalFrequency = frequency.type === DeploymentFrequencyType.External ? cloneDeep(frequency) : undefined;
    if (this.externalFrequency) {
      return;
    }

    formData.checksPerDay = frequency.count;
    formData.timeout = frequency?.timeout || 600;
    formData.timeout_unit = frequency?.timeout_unit || 'seconds';
    formData.reminderNotification = frequency.reminder ? 1 : 0;
    formData.remindWhen = frequency.remindWhen;
    formData.remindWhen_unit = frequency?.remindWhen_unit ?? 'hours';
    formData.remindWhat = frequency.remindWhat;
    formData.remindWho = frequency.remindWho;

    if (!formData.remindWho) {
      formData.remindWho = [-1];
    }

    if (includes([DeploymentFrequencyType.Daily, DeploymentFrequencyType.Weekly], formData.frequency)) {
      formData[formData.frequency] = frequency.days;
    } else if (formData.frequency === 'once') {
      const frequencyOnceTimeField = find(this.fields, {name: 'frequencyOnceTime'});

      formData.frequencyOnceTime = {
        startDate: this.utils.toMilliseconds(frequency.availableAt),
        endDate: this.utils.toMilliseconds(frequency.availableUntil)
      };
      (<any>frequencyOnceTimeField).inputs.dateRange = formData.frequencyOnceTime;
    } else if (formData.frequency !== DeploymentFrequencyType.Custom) {
      formData[formData.frequency] = frequency.when;
    }

    if (frequency.type === DeploymentFrequencyType.Daily) {
      if (frequency.count > 1) {
        formData.multiStartingTime = frequency?.startingTime ?? '00:00';
        formData.multiInterval = frequency.interval;
        formData.multiDuration = frequency.duration;
      } else {
        if (has(frequency, 'startingTime')) {
          if (isNumber(frequency.startingTime)) {
            if (frequency.startingTime < 10) {
              formData.startingTime = `0${frequency.startingTime}:00`;
            } else {
              formData.startingTime = `${frequency.startingTime}:00`;
            }
          } else {
            formData.startingTime = frequency.startingTime;
          }
          if (isNumber(frequency.duration)) {
            if (!frequency.duration) {
              formData.duration = '23:59';
            } else {
              if (frequency.duration < 10) {
                formData.duration = `0${frequency.duration}:00`;
              } else {
                formData.duration = `${frequency.duration}:00`;
              }
            }
          } else {
            formData.duration = frequency.duration;
          }
        } else {
          formData.startingTime = '00:00';
          formData.duration = '23:59';
        }
      }
    }

    if (frequency.type !== 'onDemand') {
      if (frequency.count < 0) {
        formData.atLogin = 1;

        if (frequency.count === -2) {
          formData.atLoginRequiredResponse = 1;
        }
      } else {
        formData.atLogin = 0;
      }
    }

    if (frequency.type === 'custom') {
      // map in the custom fields
      formData.startingDateTime = frequency?.startingDate ?? '';
      if (formData.startingDateTime) {
        formData.startingDateTime = this.utils.toMilliseconds(formData.startingDateTime);
      }
      formData.customDuration = frequency?.duration ?? 'PT00H00M';
      formData.customInterval = frequency?.interval ?? 'PT00H00M';
      if (formData.customInterval === '-1') {
        formData.customRedeployImmediately = 1;
        formData.customInterval = 'PT00H00M';
      } else {
        formData.customRedeployImmediately = 0;
      }
    }

    if (frequency.type === DeploymentFrequencyType.FromProperty) {
      this.selectedPropertyValues = [];
      formData.fromPropertyUntilCompleted = frequency?.untilCompleted;
      formData.propertyValues = keys(frequency.filters);

      if (!formData.fromPropertyUntilCompleted) {
        formData.fromPropertyDuration = frequency?.duration ?? 'PT00H00M';
      }

      each(frequency.filters, (value: OutputValue, propertyID) => {
        formData[propertyID] = this.propertyService.decodePropertyCondition(propertyID, value);

        this.selectedPropertyValues.push(propertyID);
      });
    }
  }

  private addAssignmentGroups() {
    const groups: ICheckGroup[] = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];
    const groupFields = map(groups, (group: ICheckGroup, index: number) => {
      index++;

      return [
        {
          containerClass: 'deploy-field assets zones workers assign-field-group start-group',
          title: group.title || `${this.translate.instant('MGMT_DETAILS.Group')} ${index}`,
          type: 'subtitle'
        },
        {
          containerClass: 'deploy-field assets zones workers assign-field-group',
          title: 'SHARED.Team(s)',
          name: `assignedTo_group${index}_teams`,
          type: 'selectmenu',
          placeholder: this.translate.instant('SHARED.Any_Team'),
          multiple: true,
          required: false,
          searchable: true,
          canClear: true,
          valueProperty: 'groupID',
          originalOrder: true,
          options: this.getTeamList(this.getUserLocations()),
          test: (team: any) => !get(team, 'disabledAt'),
          func: (team: any) => this.teamsService.teamName(team),
          onChange: () => this.syncUsersField(true, index)
        },
        {
          containerClass: 'deploy-field assets zones workers assign-field-group',
          title: 'SHARED.EDIT_Permissions',
          name: `assignedTo_group${index}_permissions`,
          type: 'selectmenu',
          placeholder: this.translate.instant('SHARED.Any_Permissions'),
          multiple: true,
          required: false,
          searchable: true,
          canClear: true,
          valueProperty: 'id',
          options: this.permissionsService.permissions.data,
          onChange: () => this.syncUsersField(true, index),
          test: (ref) => this.filterPermission(ref)
        },
        // {
        //   containerClass: 'deploy-field assets zones workers assign-field-group',
        //   name: `assignedTo_group${index}_certifications`,
        //   title: 'SHARED.Certification(s)',
        //   placeholder: this.translate.instant('SHARED.Any_Certification'),
        //   type: 'selectmenu',
        //   multiple: true,
        //   valueProperty: 'certificationID',
        //   options: this.certificationsService.certifications,
        //   test: (ref) => !get(ref, 'disabledAt'),
        //   func: (certification: any) => this.formBuilderService.certificationName(certification)
        // },
        {
          containerClass: 'deploy-field assets zones workers assign-field-group',
          name: `assignedTo_group${index}_roles`,
          title: 'MGMT_DETAILS.Roles',
          type: 'selectmenu',
          placeholder: this.translate.instant('MGMT_DETAILS.Any_Role'),
          multiple: true,
          valueProperty: 'roleID',
          options: this.roleService.roles,
          test: (ref) => !get(ref, 'disabledAt'),
          func: (role: any) => this.roleService.roleName(role),
          onChange: () => this.syncUsersField(true, index)
        },
        {
          containerClass: 'deploy-field assets zones workers assign-field-group end-group',
          title: 'SHARED.User(s)',
          name: `assignedTo_group${index}_users`,
          type: 'selectmenu',
          multiple: true,
          placeholder: this.translate.instant('SHARED.Any_User'),
          valueProperty: 'userID',
          originalOrder: true,
          options: this.getUserList(this.getUserLocations()),
          func: (ref) => this.getUserName(ref),
          test: (ref) => !get(ref, 'disabledAt'),
          onChange: (values) => null
        }
      ];
    });
    const index = findIndex(this.fields, {name: 'divideAssignment'}) + 1;

    if (groupFields.length) {
      this.fields = [
        ...this.fields.slice(0, index),
        ...flatten(groupFields),
        ...this.fields.slice(index)
      ];
    }
  }

  private addCheckGroups() {
    const theCheck = this.checkService.getCheck(this.deploymentPage.checkId);
    const groups: ICheckGroup[] = get(theCheck, 'checkGroups') || [];

    const groupFields = map(groups, (group: ICheckGroup, index: number) => {
      index++;
      const myOptions = [];
      const gData = groups[index - 1];
      each(gData.questions, (item) => {
        const theQuestion = this.checkService.questions[item];
        if (theQuestion) {
          // this question is still in the check
          myOptions.push({
            id: item,
            description: this.userService.translateItem(theQuestion, 'title')
          });
        }
      });
      if (! has(this.questionsToInclude, gData.groupID) ) {
        this.questionsToInclude[gData.groupID] = clone(myOptions);
      }

      // Parse translations from JSON string if needed
      const groupForTranslation = group.translations && typeof group.translations === 'string'
        ? {...group, translations: JSON.parse(group.translations)}
        : group;
      const translatedTitle = this.userService.translateItem(groupForTranslation, 'title');

      return [
        {
          containerClass: 'deploy-field assets zones workers question-field-group start-group',
          title: translatedTitle ? `${this.translate.instant('MGMT_DETAILS.Group')}: ${translatedTitle}` : `${this.translate.instant('MGMT_DETAILS.Group')} ${index}`,
          type: 'subtitle'
        },
        {
          containerClass: 'deploy-field assets zones workers question-field-group end-group',
          name: `deployment_group${index}_questions`,
          required: false,
          type: 'customElement',
          component: QuestionsToIncludeFormComponent,
          inputs: {
            options: myOptions,
            theCheck,
            groupID: group.groupID,
            selectedItems: this.questionsToInclude[group.groupID]
          },
          outputs: {
            onChanged: (selectedItems) => {
              const field = <FormField>find(this.fields, {name: `deployment_group${index}_questions`});
              (field.componentRef as QuestionsToIncludeFormComponent).selectedItems = selectedItems;
              this.questionsToInclude[group.groupID] = selectedItems;
            }
          }
        },
      ];
    });

    const index = findIndex(this.fields, {name: 'customQuestionSelection'}) + 1;

    if (groupFields.length) {
      this.fields = [
        ...this.fields.slice(0, index),
        ...flatten(groupFields),
        ...this.fields.slice(index)
      ];
    }
  }

  private checkRequiredAllGroupsField(): void {
    const checkGroups = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];

    if (checkGroups.length < 2) {
      const formPrefix: string = this.formPrefix;
      const groupsFieldSelector = `#${formPrefix}requireAllGroups_container`;
      const divideAssignmentFieldSelector = `#${formPrefix}divideAssignment_container`;

      this.nativeElementService.setElementVisibilityBy(this.nativeElement, groupsFieldSelector, false);
      this.nativeElementService.setElementVisibilityBy(this.nativeElement, divideAssignmentFieldSelector, false);
    }
  }

  private onLocationChange(locationIds: any[] = []) {
    locationIds = isEmpty(locationIds) ? this.getUserLocations() : map(locationIds, Number);

    if (this.deploymentType === 'assets') {
      this.updateFieldByName('zones', this.zoneService.buildZoneList(locationIds, null, false));
    } else if (this.deploymentType === 'zones') {
      const items = this.zoneService.buildZoneList(locationIds, null, false);
      items.unshift({id: -1, description: this.translate.instant('SHARED.All_Zones')});
      this.updateFieldByName('zones', items);
    } else if (this.deploymentType === 'workers') {
      this.updateFieldByName('teams', this.getTeamList(locationIds));
      this.syncUsersField();
      this.syncRemindWho();
    }

    this.updateAssignFieldsData();
  }

  private async updateAssignFieldsData() {
    let locations: number[] = map(<any>this.getFieldValue('locations'), Number) || [];
    if (isEmpty(locations)) {
      locations = this.getUserLocations();
    }

    await this.loadingService.enable();
    const divideAssignmentField: any = find(this.fields, {name: 'divideAssignment'});
    if (divideAssignmentField && divideAssignmentField.getValue()) {
      this.updateAssignmentGroups();
    } else {
      this.updateFieldByName('teams', this.getTeamList(locations));
      this.updateFieldByName('assignedTo_teams', this.getTeamList(locations));
      this.syncUsersField(true);
    }
    await this.loadingService.disable();
  }

  private syncAssetsField() {
    const locations: number[] = map(<any>this.getFieldValue('locations'), Number) || [];
    let zones: any = this.getFieldValue('zones') || [];

    zones = map(zones, (zone: string) => {
      if (isNaN(+zone)) {
        return +last(split(zone, ':'));
      } else {
        return +zone;
      }
    });

    const options = filter(this.getAssetsByCurrentLocations(), (asset: any) => {
      const isValidByLocation: boolean = includes(locations, asset.location) || isEmpty(locations);
      const isValidByZone: boolean = includes(zones, asset.zone) || isEmpty(zones);

      return isValidByLocation && isValidByZone;
    });

    return options;
  }

  private resetAssetsFolders() {
    const assetFoldersField = find(this.fields, { name: 'assetsFolders' }) as FormField;
    const folderPickerComponent = assetFoldersField?.componentRef as FolderPickerComponent;

    if (folderPickerComponent) {
      folderPickerComponent.resetSelection();
    }
  }

  private syncUsersField(isAssignBlock: boolean = false, index?: number) {
    let fieldPrefix = 'assignedTo_';
    if (!isUndefined(index)) {
      fieldPrefix += `group${index}_`;
    }

    const fieldNames = {
      teams: isAssignBlock ? `${fieldPrefix}teams` : 'teams',
      permissions: isAssignBlock ? `${fieldPrefix}permissions` : 'permissions',
      users: isAssignBlock ? `${fieldPrefix}users` : 'users'
    };

    let locations: number[] = map(<any>this.getFieldValue('locations'), Number) || [];
    let teams: number[] = map(<any>this.getFieldValue(fieldNames.teams), Number) || [];

    if (isEmpty(locations)) {
      locations = this.getUserLocations();
    }

    if (includes(teams, -1)) {
      const userIds: number[] = map(<any>this.getFieldValue('users'), Number);
      const primaryGroups: number[] = uniq(map(userIds, (userId: number) => get(this.accountService.getByID(userId), 'primaryGroup')));
      teams = [...teams, ...primaryGroups];
    }

    let permissions: string[] = (<string[]>this.getFieldValue(fieldNames.permissions)) || [];

    if (this.deploymentType === 'workers') {
      permissions = <string[]>this.filterWorkerOptions(permissions);
      teams = <number[]>this.filterWorkerOptions(teams);
    }

    const users: any[] = filter(this.getUserList(locations), (user) => {
      const userPermissions: string[] = keys(user.permissions);
      const isPermissionEmpty: boolean = isEmpty(userPermissions) || isEmpty(permissions);
      const isValidByTeam: boolean = intersection(user.groups, teams).length > 0 || isEmpty(user.groups) || isEmpty(teams);
      const isValidByPermission: boolean = intersection(userPermissions, permissions).length > 0 || isPermissionEmpty;

      return isValidByTeam && isValidByPermission;
    });

    this.updateFieldByName(fieldNames.users, users);
  }

  private updateFieldByName(fieldName: string, options: any[]) {
    if (!this.hasFormConfig) { return; }   // CVX-6062: bail if torn down
    const fieldId = `#${this.formPrefix}${fieldName}`;
    const fieldConfig: any = find(this.fields, {name: fieldName});
    const fieldSelectedValue: any = this.getFieldValue(fieldName);

    fieldConfig.options = options;
    this.formBuilderService.replaceSelectOptionsWith(fieldId, (<any>this.deploymentPage).formConfig, fieldConfig, {[fieldName]: fieldSelectedValue});
  }

  private syncRemindWho() {
    // are there any explicit user assignments
    const explicit: boolean = (<string[]>this.getFieldValue('assignedTo_users') || []).length > 0;

    const options = this.getNotificationList(this.getUserLocations(), explicit);
    this.updateFieldByName('remindWho', options);
  }

  // Guarded read: formConfig can be momentarily undefined when an async field
  // onChange fires during page teardown/re-init (CVX-6062). Defaults to '' (no NULL).
  private get formPrefix(): string {
    return get(this.deploymentPage, 'formConfig.prefix', '');
  }

  // CVX-6062: deploymentPage/formConfig can be torn down before an async field
  // handler (onChange/setTimeout/reminder sync) fires. Those handlers dereference
  // formConfig and are no-ops without it — callers bail on false rather than throw.
  private get hasFormConfig(): boolean {
    return !isEmpty(get(this.deploymentPage, 'formConfig'));
  }

  private getFieldValue(name: string, extractFromField: boolean = false): any {
    const prefix: string = this.formPrefix;
    const fieldId = `#${prefix}${name}`;
    if (extractFromField) {
      const remindWhat: any = find(this.fields, {name});
      if (remindWhat && remindWhat.getValue) {
        return remindWhat.getValue();
      }
    }
    return $(fieldId).val();
  }

  private onDivideAssignmentGroupsChange() {
    const divideAssignmentField: any = find(this.fields, {name: 'divideAssignment'});

    if (divideAssignmentField && divideAssignmentField.getValue) {
      const divideAssignmentValue: any = divideAssignmentField.getValue();

      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.assign-field', !divideAssignmentValue);
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.assign-field-group', divideAssignmentValue);
      this.updateAssignFieldsData();
    } else {
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.assign-field', true);
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.assign-field-group', false);
      this.updateAssignFieldsData();
    }
  }

  private updateAssignmentGroups() {
    let locationIds: number[] = map(<any>this.getFieldValue('locations'), Number) || [];
    if (isEmpty(locationIds)) {
      locationIds = this.getUserLocations();
    }
    const teams: any[] = this.getTeamList(locationIds);
    const checkGroups = get(this.checkService.getCheck(this.deploymentPage.checkId), 'checkGroups') || [];

    each(checkGroups, (checkGroup, index: number) => {
      index++;
      this.updateFieldByName(`assignedTo_group${index}_teams`, teams);
      this.syncUsersField(true, index);
    });
  }

  private onFrequencyChange() {
    const value: string = <string>this.getFieldValue('frequency');
    this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.frequency-field', false);
    this.formBuilderService.setElementVisibilityBy(this.nativeElement, `.${this.deploymentType}.frequency-field.${value}`, true);
    this.onSendReminderChange(value);
  }

  private definePresentationPreviewControl() {
    const buttonTitle: string = this.translate.instant('SHARED.Preview');
    this.nativeElement
      .querySelector('.presentation-type-field')
      .insertAdjacentHTML('beforeend', '<ion-button class="page-button" color="light">${buttonTitle}</ion-button>');

    this.nativeElement.querySelector('.presentation-type-field .page-button').addEventListener('click', () => {
    });
  }

  private defineCompletedCheckField() {
    const element = `
      <div class="completed-check-title-block">
        <div class="title">${this.translate.instant('MGMT_DETAILS.Action')}</div>
        <div class="title">${this.translate.instant('MGMT_DETAILS.Point_Value')}</div>
      </div>
    `;

    this.nativeElement
      .querySelector('.completed-check-field')
      .insertAdjacentHTML('beforebegin', element);
  }

  private onTimesPerDayChange(value: any = 1) {
    const theField: FormField = find(this.fields, {name: 'frequency'});
    if (theField && theField.getValue() === 'daily') {
      const freqFieldClass = (+value === 1) ? 'daily' : 'multi-daily';
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, '.frequency-field', false);
      this.formBuilderService.setElementVisibilityBy(this.nativeElement, `.${this.deploymentType}.frequency-field.${freqFieldClass}`, true);
      this.formBuilderService.disableFieldByName(this.nativeElement, 'availableImmediately', false);
    }
  }

  private updateRemindWhatField(opts: any) {
    if (!this.hasFormConfig) { return; }   // CVX-6062: bail if torn down
    const theField: any = find(this.fields, {name: 'remindWhat'});

    if (theField && theField.getValue) {
      const otherField: any = find(this.fields, {name: 'reminderNotification'});
      let reminderValue = false;
      if (otherField && otherField.getValue) {
        reminderValue = otherField.getValue();
      }
      const data = {
        remindWhat: theField.getValue()
      };

      const field = <any>find((<any>this.deploymentPage).formConfig.fields, {name: 'remindWhat'});
      // CVX-6064: formConfig may be present but lack 'remindWhat' — only mutate when found.
      // The !reminderValue and updateFields paths previously dereferenced an undefined field.
      if (field) {
        field.options = opts;
        field.disabled = false;
        field.required = true;
        if (!reminderValue) {
          field.disabled = true;
          field.required = false;
        }
        this.formBuilderService.updateFields((<any>this.deploymentPage).formConfig, [field], data);
      }
    }
  }

  private onSendReminderChange(frequency?: string) {
    // rebuild the current field based upon the frequency
    if (frequency !== undefined) {
      const opts = [];
      each(this.reminderTriggers, ref => {
        if (has(ref, 'exclude') && indexOf(ref.exclude, frequency) > -1) {
          // skip this item
          this.logger.debug('leaving out ' + ref.id);
        } else {
          opts.push(ref);
        }
      });
      this.updateRemindWhatField(opts);
    }

    const theField: any = find(this.fields, {name: 'reminderNotification'});
    if (theField && theField.getValue) {
      const theValue = theField.getValue();
      this.formBuilderService.disableFieldByName(this.nativeElement, 'remindWhat', !theValue);
      this.formBuilderService.disableFieldByName(this.nativeElement, 'remindWhen', !theValue);
      this.formBuilderService.disableFieldByName(this.nativeElement, 'remindWho', !theValue);
      if (theValue) {
        // notifications are enabled, what about the expiring window?
        this.onTriggerChange();
      }
    }
  }

  private onTriggerChange(values: any = [], theForm?: any) {
    const sels = this.getFieldValue('remindWhat', true);
    if (indexOf(sels, 'expiring') > -1) {
      this.formBuilderService.disableFieldByName(this.nativeElement, 'remindWhen', false);
    } else {
      this.formBuilderService.disableFieldByName(this.nativeElement, 'remindWhen', true);
    }
  }

  private onCheckHistoryChange() {
    const divideAssignmentField: any = find(this.fields, {name: 'showCheckHistory'});
    if (divideAssignmentField && divideAssignmentField.getValue) {
      this.formBuilderService.disableFieldByName(this.nativeElement, 'previousChecks', !divideAssignmentField.getValue());
    }
  }

  private getUserList(locations: number[], includeSpecials: boolean = false): any[] {
    const users = this.accountService.getUserlist(locations);

    if (this.deploymentType === 'workers' || includeSpecials) {
      users.unshift(
        this.accountService.createAccountObject({
          userID: -1,
          firstname: this.translate.instant('MGMT_DETAILS.Self_Check')
        }),
        this.accountService.createAccountObject({
          userID: -2,
          firstname: this.translate.instant('MGMT_DETAILS.User_Supervisor')
        })
      );
    }

    return users;
  }

  private getNotificationList(locations: number[], includeSpecials: boolean = false): any[] {
    const users = this.accountService.getUserlist(locations);

    if (includeSpecials) {
      users.unshift(
        this.accountService.createAccountObject({
          userID: -1,
          firstname: this.translate.instant('MGMT_DETAILS.Assignees')
        }),
        this.accountService.createAccountObject({
          userID: -2,
          firstname: this.translate.instant('MGMT_DETAILS.Assignee_Supervisors')
        })
      );
    }
    return users;
  }

  private getTeamList(locations: number[]): any[] {
    const teams: any[] = orderBy(this.teamsService.getTeamList(locations), 'name');
    if (this.deploymentType === 'workers') {

      teams.unshift(
        {groupID: -2, name: this.translate.instant('SHARED.Any_Team')},
        {groupID: -1, name: this.translate.instant('MGMT_DETAILS.Workers_Primary_Team')}
      );
    }

    return teams;
  }

  private getUserName(userData: any): string {
    let name = this.userService.getFullname(userData.userID);
    if (userData.userID === -1 || (name === 'unknown' && userData.firstname)) {
      name = userData.firstname;
    }
    return name;
  }

  private getChecksCountOptions() {
    const options: any[] = times(24, (currentNumber: number) => {
      const id: string = (currentNumber + 1).toString();
      return {id, description: id};
    });

    options.unshift({id: -1, description: this.translate.instant('MGMT_DETAILS.At_Login')});

    return options;
  }

  private getStartingTimes() {
    const options: any[] = times(24, (currentNumber: number) => {
      let id: string = (currentNumber).toString();
      if (currentNumber === 0) {
        id = this.translate.instant('MGMT_DETAILS.Midnight');
      } else if (currentNumber === 12) {
        id = this.translate.instant('MGMT_DETAILS.Noon');
      }
      return {id: currentNumber, description: id};
    });

    return options;
  }

  public getDurations(addSuffix: boolean = false) {
    const options: any[] = times(23, (currentNumber: number) => {
      currentNumber++;
      let desc: string = (currentNumber).toString();
      if (currentNumber === 1) {
        if (addSuffix) {
          desc = this.translate.instant('MGMT_DETAILS.1_Hour_Before');
        } else {
          desc = this.translate.instant('SHARED.1_HOUR');
        }
      } else {
        if (addSuffix) {
          desc += ' ' + this.translate.instant('MGMT_DETAILS.Hours_Before');
        } else {
          desc += ' ' + this.translate.instant('SHARED.Hours');
        }
      }
      return {id: currentNumber, description: desc};
    });

    options.push({
      id: 0,
      description: '24 ' + this.translate.instant('SHARED.Hours')
    });

    return options;
  }

  private filterPermission(permission): boolean {
    permission.description = this.translate.instant(permission.description);

    if (permission.id === 'corvex') {
      return get(this.userDataService.Permissions, 'corvex');
    } else if (permission.id === 'sadmin') {
      return get(this.userDataService.Permissions, 'sadmin') || get(this.userDataService.Permissions, 'corvex');
    } else {
      return true;
    }
  }

  private updateZonesField(): void {
    const zonesField = find(this.fields, {name: 'zones'});

    if (zonesField) {
      const isAllZonesAdded = find(zonesField.options, { id: -1 });

      if (!isAllZonesAdded) {
        zonesField.originalOrder = true;
        zonesField.options.unshift({id: -1, description: this.translate.instant('SHARED.All_Zones')});
      }

      zonesField.required = true;
      zonesField.placeholder = this.translate.instant('SHARED.Choose_Zones');

      this.updateFieldByName('zones', zonesField.options);
    }
  }

  private updateFieldLocationData(): void {
    const locations: number[] = this.getUserLocations();
    const locationOptions = this.userService.getUserLocations(locations, false);

    const userTeamsOptions = this.getTeamList(locations);
    const userListOptions = this.getUserList(locations);
    this.setFieldOptions('locations', locationOptions);
    this.setFieldOptions('teams', userTeamsOptions);
    this.setFieldOptions('assignedTo_teams', userTeamsOptions);
    this.setFieldOptions('users', userListOptions);
    this.setFieldOptions('assignedTo_users', userListOptions);
    this.setFieldOptions('zones', this.zoneService.getGroupedZonesByLocations(locations));
    this.updateLocationsAccessIndicator(locationOptions);
    this.syncRemindWho();
  }

  private setFieldOptions(name: string, options: any[]) {
    const field = find(this.fields, {name});

    if (field) {
      field.options = options;
    }
  }

  private getUserLocations(): number[] {
    const check: ICheck = this.checkService.getCheck(get(this.deploymentPage, 'checkId'));
    return get(check, 'locations.length') ? map(check.locations, Number) : this.userDataService.locations;
  }

  private updateLocationsAccessIndicator(locationOptions: unknown[] = []): void {
    const locationsField: FormField = find(this.fields, {name: 'locations'});

    if (!locationsField) {
      return;
    }

    locationsField.placeholder = this.translate.instant('SHARED.All_Locations');
    locationsField.tooltip = '';

    // Non-empty userdata locations means this user is restricted to specific locations.
    if (this.userDataService.locations?.length > 0) {
      const accessibleLocationNames: string[] = map(locationOptions, 'name');
      const accessibleLocationsLabel: string = accessibleLocationNames.join(', ');
      const suffix = accessibleLocationsLabel || this.translate.instant('SHARED.No_Data');

      locationsField.placeholder = `${this.translate.instant('SHARED.All_Locations')} (${this.translate.instant('SHARED.Limited_By_Your_Access')})`;
      locationsField.tooltip = `${this.translate.instant('SHARED.All_Locations_Will_Deploy_Only_To')} ${suffix}`;
    }
  }

  private getAssetsByCurrentLocations() {
    const locations: number[] = this.getUserLocations();
    return this.assetsService.getAssetsByLocations(locations, false);
  }

  private checkFrequencyField() {
    if (!this.hasFormConfig) { return; }   // CVX-6064: bail if torn down (guards getData below)
    const data = (<any>this.deploymentPage).getData();
    const check = this.checkService.getCheck(get(this.deploymentPage, 'checkId'));

    if (get(check, 'checkGroups.length') === 1) {
      const frequencyField = find(this.fields, {name: 'frequency'});

      if (frequencyField) {
        frequencyField.options.push({
          id: 'onDemand',
          description: this.translate.instant('MGMT_DETAILS.On_Demand_Option')
        });
      }
    }

    if (data?.propertyValues?.length && data?.frequency === DeploymentFrequencyType.FromProperty) {
      const matchFields = filter(map(data.propertyValues, (propertyID: string) => {
        return find(this.propertyValueFields, { name: propertyID });
      }));
      const indexOfPropertyValuesField = findIndex(this.fields, { name: 'propertyValues' });
      const includePropertyValueFields = some(this.fields, (field) => {
        return includes(field.name, this.fieldPropertyValuePrefix);
      });

      if (!includePropertyValueFields && indexOfPropertyValuesField >= 0) {
        this.fields.splice(indexOfPropertyValuesField + 1, 0, ...matchFields);
      }
    }
  }

  private prepareWorkerFields() {
    if (!this.hasFormConfig) { return; }   // CVX-6062: bail if torn down (also guards getData below)
    const fields = [];
    const data = (<any>this.deploymentPage).getData();
    const fieldsMap: { [fieldKey: string]: string } = {
      assignedTo_teams: this.translate.instant('MGMT_DETAILS.Choose_Team'),
      assignedTo_permissions: this.translate.instant('MGMT_DETAILS.Choose_Permission'),
      assignedTo_users: this.translate.instant('SHARED.Any_User')
    };

    each(fieldsMap, (placeholder, name) => {
      const field = <any>find((<any>this.deploymentPage).formConfig.fields, {name});
      // CVX-6064: formConfig may be present but partial — skip a field that isn't there
      // rather than throwing 'Cannot set properties of undefined'.
      if (!field) { return; }
      field.required = name === 'assignedTo_users' ? false : true;
      field.placeholder = placeholder;

      if (name === 'assignedTo_permissions') {
        const values = data.assignedTo_permissions || [];

        field.options = [
          {
            id: -2,
            description: this.translate.instant('SHARED.Any_Permission'),
            type: 'boolean',
            disabled: values.length && !includes(values, '-2')
          },
          ...orderBy(this.permissionsService.permissions.data, 'description')
        ];
        field.originalOrder = true;
      }
      fields.push(field);
    });
    this.formBuilderService.updateFields((<any>this.deploymentPage).formConfig, fields, data);
  }

  private removeWorkerAssignAnyOption(ids: string[], fieldName): void {
    const anyOptionId = '-2';

    if (this.deploymentType === 'workers') {
      const fieldId = `#${this.formPrefix}${fieldName}`;
      const optionElement = $(`${fieldId} option[value='${anyOptionId}']`);
      const selected = optionElement.data('data') || {};

      if (includes(ids, anyOptionId)) {
        if (ids.length > 1) {
          const values = <string[]>this.filterWorkerOptions(ids);
          $(fieldId).val(values).trigger('change');
          selected.disabled = true;
        }
      } else if (ids.length === 0) {
        optionElement.prop('disabled', false);
        selected.disabled = false;
        $(fieldId).trigger('select2:select');
      } else {
        selected.disabled = true;
      }
    }
  }

  private filterWorkerOptions(options: string[] | number[]): string[] | number[] {
    return <string[] | number[]>reject(options, (option) => +option === -2);
  }

  public getDynamicNumericPropertyFields() {
    return this.propertyService.getPropertyValuesFields(this.fieldPropertyValuePrefix, {
      containerClass: 'custom-data-field removable deploy-field assets zones workers frequency-field fromProperty',
      onRemoveField: (field: FormField) => this.onRemoveField(field)
    });
  }

  public onRemoveField(field: FormField) {
    this.selectedPropertyValues = remove(this.selectedPropertyValues, (item) => item !== field.name);
    const propertyValuesField = find(this.fields, {name: 'propertyValues'});
    const selectFiltersFormComponent = propertyValuesField.componentRef as SelectFiltersFormComponent;

    const originalValue = selectFiltersFormComponent.selectedItems;
    selectFiltersFormComponent.selectedItems = this.selectedPropertyValues;
    selectFiltersFormComponent.formValue = this.selectedPropertyValues;
    this.formBuilderService.markFieldAsChanged(`#${this.formPrefix}Form`, 'propertyValues', originalValue, this.selectedPropertyValues);
  }
}
